/**
 * EmailRoutesPanel — the master control for every outbound email route (superadmin).
 *
 * One toggle per Resend send-site. A route only actually sends when its toggle is ON
 * *and* the mail service is configured; a send site whose route is off skips with an
 * honest signal, never a faked send. All routes default OFF (clean slate) — enabling
 * an external/customer-facing route is confirmed first, since it starts real email.
 *
 * Design: a single bordered card, one divided row per route — label + description on
 * the left, status notes (env override / mail not configured) inline, the switch on
 * the right. Monochrome with the one HT-green accent on the confirm action; amber only
 * where it carries meaning (an env override the operator can't change here).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Mailbox } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'react-toastify';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getEmailRoutes, setEmailRoute, type EmailRoute } from '@/lib/emailRoutesApi';

/** Which Pocket builds each APNs environment serves, in words staff know. */
const PUSH_DEVICES: Record<string, string> = {
  production: 'App Store and TestFlight',
  sandbox: 'Xcode development',
};

const formatWhen = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** @param onChanged called after a switch is saved (the Alert summary card re-reads its status). */
export function EmailRoutesPanel({ onChanged }: { onChanged?: () => void } = {}) {
  const [routes, setRoutes] = useState<EmailRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [pending, setPending] = useState<EmailRoute | null>(null); // external route awaiting enable-confirm

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEmailRoutes();
      setRoutes(res.routes);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load email routes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const commit = async (route: EmailRoute, next: boolean) => {
    setSavingKey(route.key);
    setRoutes((prev) => prev.map((r) => (r.key === route.key ? { ...r, enabled: next } : r))); // optimistic
    try {
      const res = await setEmailRoute(route.key, next);
      setRoutes((prev) => prev.map((r) => (r.key === route.key ? { ...r, enabled: res.enabled } : r)));
      toast.success(`${route.label} — email ${res.enabled ? 'on' : 'off'}`);
      onChanged?.();
    } catch (err) {
      setRoutes((prev) => prev.map((r) => (r.key === route.key ? { ...r, enabled: !next } : r))); // revert
      toast.error(err instanceof Error ? err.message : 'Failed to update route');
    } finally {
      setSavingKey(null);
    }
  };

  // Enabling an external route confirms first; turning any route off is immediate.
  const onToggle = (route: EmailRoute, next: boolean) => {
    if (next && route.external) setPending(route);
    else void commit(route, next);
  };

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Mailbox className="h-4 w-4 text-[#8FB560]" />
        <h3 className="text-sm font-semibold text-foreground">Email routes</h3>
      </div>

      <p className="px-4 pt-3 text-sm text-muted-foreground">
        Master control for every kind of outbound email. Each is off until you turn it
        on; a route only sends when its switch is on and the mail service is configured.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <ul className="mt-2 divide-y divide-border">
          {routes.map((route) => {
            const envForced = route.envOverride !== null;
            const readOnly = route.canChange === false;
            const checked = route.launchLocked ? false : (envForced ? route.envOverride! : route.enabled);
            return (
              <li key={route.key} className="flex items-start justify-between gap-4 px-4 py-3.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{route.label}</span>
                    {route.external && (
                      <span className="rounded-sm border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        External
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{route.description}</p>
                  {envForced && !route.launchLocked && (
                    <p className="mt-1 text-xs text-amber-500">
                      Forced {route.envOverride ? 'on' : 'off'} by an environment variable — read-only here.
                    </p>
                  )}
                  {checked && !route.keyConfigured && (
                    <p className="mt-1 text-xs text-amber-500">
                      {route.channel === 'push'
                        ? 'On, but Apple push isn’t set up yet — nothing sends until the APNs keys are added.'
                        : 'On, but the mail service isn’t configured — nothing sends until it is.'}
                    </p>
                  )}
                  {/* Shown on or off: a superadmin test push is how the key gets checked. */}
                  {(route.pushKeyRefused ?? []).map((refusal) => (
                    <p key={refusal.environment} className="mt-1 text-xs text-amber-500">
                      Apple refused the push key for {PUSH_DEVICES[refusal.environment] ?? 'some'} devices
                      {refusal.at ? ` (last tried ${formatWhen(refusal.at)})` : ''}. Nothing reaches those devices
                      until the key is fixed; then send a test push.
                    </p>
                  ))}
                  {route.launchLocked && (
                    <p className="mt-1 text-xs text-muted-foreground">{route.lockedReason || 'Not available yet.'}</p>
                  )}
                  {readOnly && !route.launchLocked && (
                    <p className="mt-1 text-xs text-muted-foreground">Only a superadmin can turn this on or off.</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2 pt-0.5">
                  {savingKey === route.key && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  <Switch
                    checked={checked}
                    onCheckedChange={(next) => onToggle(route, next)}
                    disabled={savingKey === route.key || envForced || !!route.launchLocked || readOnly}
                    aria-label={`Enable ${route.label}`}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <AlertDialog open={pending !== null} onOpenChange={(open) => { if (!open) setPending(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn on “{pending?.label}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This starts real outbound email immediately — recipients will begin
              receiving these messages. You can turn it off again at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#8FB560] text-white hover:bg-[#7da34f]"
              onClick={() => { const r = pending; setPending(null); if (r) void commit(r, true); }}
            >
              Turn on
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
